import { select, call, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';

import { getToken } from '../selectors';
import {
  getSubscription,
  getProducts,
  requestTokenization,
  requestSubscription,
  requestUpdateCard,
  requestUpdateProduct,
} from '../../api';
import {
  doSaveProducts,
  doSaveSubscription,
  doShowLoading,
  doHideLoading,
  doHideModal,
  doShowModal,
} from '../actions';
import subscriptionStatus from '../../constants/subscriptionStatus';
import flashMessageTypes from '../../constants/flashMessageTypes';
import messages from '../../constants/messages';
import { setFlashMessage } from './flashMessage';
import { getMessageFromCode, codeSuccess, getExceptionResponse } from '../../lib/utils';
import { postMessageSender } from '../../lib/postMessager';

export function* handleFetchSubscription() {
  yield put(doShowLoading());
  const token = yield select(getToken);

  try {
    const { data } = yield call(getSubscription, token);

    yield put(doSaveSubscription(data));

    switch (data.status) {
      case subscriptionStatus.ATIVA:
        yield put(doHideModal());
        yield put(push('/editar-dados'));
        break;
      case subscriptionStatus.INATIVA:
      case 'INATIVA':
        yield call(renderProductsPage, token);
        break;
      case subscriptionStatus.ERRO:
        yield call(setFlashMessage, flashMessageTypes.error, messages.defaultError);
        break;
      default:
        yield call(setFlashMessage, flashMessageTypes.error, messages.defaultError);
        break;
    }

    postMessageSender({ action: 'started' });
  } catch (e) {
    postMessageSender({
      action: 'startFailed',
      reason: e.response ? e.response.statusText : '',
    });

    yield call(setFlashMessage, flashMessageTypes.error, messages.defaultError);
  } finally {
    yield put(doHideLoading());
  }
}

export function* handleFetchProducts() {
  yield put(doShowLoading());

  try {
    const token = yield select(getToken);
    const { data } = yield call(getProducts, token);

    const activeProducts = data.filter(p => p.status === 'ATIVO');

    yield put(doSaveProducts(activeProducts));
  } catch (e) {
    postMessageSender({
      action: 'startFailed',
      reason: e.response ? e.response.statusText : '',
    });

    yield call(setFlashMessage, flashMessageTypes.error, messages.defaultError);
  } finally {
    yield put(doHideLoading());
  }
}

export function* renderProductsPage(token) {
  yield call(handleFetchProducts, token);
  yield put(push('/assinar'));
}

export function* handleRequestSubscription(action) {
  yield put(doShowLoading());

  const sessionToken = yield select(getToken);
  const { card, product } = action.payload;
  let { token, cvv } = card;

  cvv = cvv.trim();
  token = yield call(cardTokenization, card);

  try {
    if (!token) {
      yield call(setFlashMessage, flashMessageTypes.error, messages.card.error);
      return;
    }

    const { data } = yield call(requestSubscription, sessionToken, product, token, cvv);

    if (data.code === codeSuccess) {
      yield call(setFlashMessage, flashMessageTypes.success, messages.recharge.success);

      postMessageSender({
        action: 'acquired',
        code: data.code,
        product: {
          id: product.codigo,
          name: product.nome,
        },
      });
    } else {
      yield call(setFlashMessage, flashMessageTypes.error, messages.recharge.error);

      postMessageSender({
        action: 'acquireFailed',
        code: data.code,
        product: {
          id: product.codigo,
          name: product.nome,
        },
      });
    }
  } catch (e) {
    const { data } = getExceptionResponse(e);
    const message = getMessageFromCode(data.code);

    postMessageSender({
      action: 'acquireFailed',
      reason: message.text,
      product: {
        id: product.codigo,
        name: product.nome,
      },
    });

    yield call(setFlashMessage, flashMessageTypes.error, message);
  } finally {
    yield put(doHideLoading());
  }
}

export function* cardTokenization(card) {
  const { number, validity } = card;
  const [month, year] = validity.split('/');

  try {
    const result = yield call(requestTokenization, number, month, year);

    return result.data.card.key;
  } catch (e) {}
}

export function* handleRequestUpdateCard(action) {
  yield put(doShowLoading());

  const sessionToken = yield select(getToken);
  const { card } = action;
  let { token, cvv } = card;

  token = yield call(cardTokenization, card);

  try {
    if (!token) {
      yield call(setFlashMessage, flashMessageTypes.error, messages.card.error);
      return;
    }

    const { data } = yield call(requestUpdateCard, sessionToken, token, cvv);

    if (data.code === codeSuccess) {
      postMessageSender({ action: 'cardUpdated' });

      yield call(setFlashMessage, flashMessageTypes.success, messages.edit.success);
    } else {
      postMessageSender({
        action: 'cardUpdateError',
        reason: data.code,
      });

      yield call(setFlashMessage, flashMessageTypes.error, messages.edit.error);
    }
  } catch (e) {
    postMessageSender({
      action: 'cardUpdateError',
      reason: e.response.code,
    });

    yield call(setFlashMessage, flashMessageTypes.error, messages.edit.error);
  } finally {
    yield put(doHideLoading());
  }
}

export function* handleRequestUpdateProduct(action) {
  yield put(doShowLoading());

  const sessionToken = yield select(getToken);
  const { product } = action;
  let flashMessageType;
  let flashMessageContent;

  try {
    const { data } = yield call(requestUpdateProduct, sessionToken, product);

    if (data.code === codeSuccess) {
      postMessageSender({ action: 'productUpdated' });

      flashMessageType = flashMessageTypes.success;
      flashMessageContent = messages.edit.success;
    } else {
      postMessageSender({
        action: 'productUpdateError',
        reason: data.code,
      });

      flashMessageType = flashMessageTypes.error;
      flashMessageContent = messages.edit.error;
    }
  } catch (e) {
    postMessageSender({
      action: 'productUpdateError',
      reason: e.response.code,
    });

    flashMessageType = flashMessageTypes.error;
    flashMessageContent = messages.edit.error;
  } finally {
    yield put(doHideLoading());
    yield put(doHideModal());
    yield call(setFlashMessage, flashMessageType, flashMessageContent);
    yield put(doShowModal('flash-message'));
  }
}
